import { Component, Input, Output, EventEmitter, OnInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import * as QRCode from 'qrcode';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-policy-qr-display',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="modal-overlay" (click)="onClose()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <!-- QR Code Display -->
        <div class="qr-container">
          <canvas #qrCanvas></canvas>
          @if (qrError) {
            <div class="qr-error">
              <i class="fas fa-exclamation-triangle"></i>
              <p>{{ qrError }}</p>
            </div>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.75);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
      padding: 16px;
    }

    .modal-content {
      background: white;
      border-radius: 4px;
      padding: 4px;
      width: auto;
      max-width: 90vw;
      max-height: 90vh;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
      align-items: center;
    }

    .qr-container {
      display: flex;
      justify-content: center;
      align-items: center;
      background: white;
      width: 100%;
      height: 100%;
    }

    .qr-container canvas {
      display: block;
      max-width: 100%;
      height: auto;
    }

    .qr-error {
      text-align: center;
      color: #ef4444;
      padding: 40px 20px;
    }

    .qr-error i {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .qr-error p {
      margin: 0;
      font-size: 14px;
      color: #1f2937;
    }
  `]
})
export class PolicyQrDisplayComponent implements OnInit {
  @Input() policyData: any;
  @Output() closed = new EventEmitter<void>();
  @ViewChild('qrCanvas') qrCanvas!: ElementRef<HTMLCanvasElement>;

  qrUrl = '';
  qrError = '';
  copySuccess = false;

  ngOnInit() {
    this.qrUrl = this.generateI4UUrl();
  }

  ngAfterViewInit() {
    this.generateQRCode();
  }

  /**
   * Generate I4U application URL based on environment
   */
  generateI4UUrl(): string {
    // Use i4uUrl from environment configuration
    const baseUrl = environment.i4uUrl || 'http://localhost:4201';
    
    return `${baseUrl}/application-detail/${this.policyData.applicationNo}`;
  }

  /**
   * Generate QR code on canvas
   * QR code fills the modal with 4px padding, black & white only
   */
  async generateQRCode() {
    try {
      const canvas = this.qrCanvas.nativeElement;
      
      // Calculate size based on viewport (90vw - 8px for modal padding)
      const maxSize = Math.min(window.innerWidth * 0.9 - 8, window.innerHeight * 0.9 - 8, 500);
      
      await QRCode.toCanvas(canvas, this.qrUrl, {
        width: maxSize,
        margin: 1,
        color: {
          dark: '#000000',  // Pure black
          light: '#ffffff'  // Pure white
        },
        errorCorrectionLevel: 'H'
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      this.qrError = 'Failed to generate QR code';
    }
  }

  /**
   * Copy URL to clipboard
   */
  async copyUrl() {
    try {
      await navigator.clipboard.writeText(this.qrUrl);
      this.copySuccess = true;
      setTimeout(() => {
        this.copySuccess = false;
      }, 2000);
    } catch (error) {
      console.error('Error copying URL:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = this.qrUrl;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  }

  /**
   * Download QR code as PNG
   */
  downloadQR() {
    try {
      const canvas = this.qrCanvas.nativeElement;
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `QR-${this.policyData.policyNo}.png`;
      link.href = url;
      link.click();
    } catch (error) {
      console.error('Error downloading QR code:', error);
    }
  }

  onClose() {
    this.closed.emit();
  }
}
